// Build a tree from channels using `fullname` split by '.'
// Each node: { name, path, depth, children: [], channel: null|channel, leafIds: Set<number> }
export function buildChannelTree(channels) {
  const root = { name: '__root__', path: '', depth: -1, children: [], channel: null, leafIds: new Set() };
  const index = new Map(); // path -> node
  index.set('', root);

  channels.forEach(ch => {
    const parts = String(ch.fullname ?? '').split('.').filter(Boolean);
    // Handle channels with no dot -> single part equals fullname
    const seq = parts.length ? parts : [ch.fullname];

    let prefix = '';
    let parent = root;
    seq.forEach((part, i) => {
      prefix = prefix ? `${prefix}.${part}` : part;
      let node = index.get(prefix);
      if (!node) {
        node = {
          name: part,
          path: prefix,
          depth: i,
          children: [],
          channel: null,
          leafIds: new Set(),
        };
        index.set(prefix, node);
        parent.children.push(node);
      }
      parent = node;
    });

    // mark leaf node with the channel
    parent.channel = ch;
  });

  // sort children alphabetically
  const sortRec = (node) => {
    node.children.sort((a, b) => a.name.localeCompare(b.name));
    node.children.forEach(sortRec);
  };
  sortRec(root);

  // compute leafIds sets bottom-up
  const fillLeafIds = (node) => {
    if (node.channel) node.leafIds.add(node.channel.id);
    node.children.forEach(child => {
      fillLeafIds(child);
      child.leafIds.forEach(id => node.leafIds.add(id));
    });
  };
  fillLeafIds(root);

  return root;
}

// Filter tree by text (case-insensitive). Returns a *new* pruned tree (same shape).
// A node is kept if it or any descendant matches the filter. Matching is on:
// - channel.fullname (for leaves with channels)
// - node.path (for groups)
export function filterTree(root, filterText) {
  const f = String(filterText || '').trim().toLowerCase();
  if (!f) return root;

  const cloneIf = (node) => {
    const selfStr = (node.channel?.fullname || node.path).toLowerCase();
    const selfMatches = selfStr.includes(f);

    const keptChildren = [];
    node.children.forEach(ch => {
      const kid = cloneIf(ch);
      if (kid) keptChildren.push(kid);
    });

    if (!selfMatches && keptChildren.length === 0) return null;

    // shallow clone
    const copy = {
      name: node.name,
      path: node.path,
      depth: node.depth,
      children: keptChildren,
      channel: node.channel,
      leafIds: new Set(node.leafIds), // keep to compute tri-state easily
    };
    return copy;
  };

  const filtered = cloneIf(root);
  return filtered || { ...root, children: [] };
}

// Expand all nodes if filter is active (for visibility). Otherwise respect user toggles.
export function collectAllPaths(node, acc = new Set()) {
  node.children.forEach(ch => {
    acc.add(ch.path);
    collectAllPaths(ch, acc);
  });
  return acc;
}
